/**
 * Channels route factory. The shell spreads these under the authed route:
 *
 *   children: [...channelsRoutes(authedRoute)]
 *
 * Pages stay router-free; this file is the only place channel code touches the
 * router and the only place it reads the session. Every control is gated on
 * the exact permission the generated route policy demands — including the two
 * audited ones, the raw-payload reveal and the source-pool save.
 */
import { createRoute, useNavigate, type AnyRoute } from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { ChannelsPage } from "./pages/channels-page";
import { ChannelOrdersPage } from "./pages/channel-orders-page";
import { ReconciliationPage } from "./pages/reconciliation-page";
import { CHANNEL_PERMISSIONS as P, ChannelPermissionBoundary } from "./permissions";

function ChannelsRoute() {
  const { hasPermission } = useSession();
  return (
    <ChannelPermissionBoundary permission={P.channelRead}>
      <ChannelsPage
        canReadSourceSet={hasPermission(P.sourceSetRead)}
        canManageSourceSet={hasPermission(P.sourceSetManage)}
        canReadWarehouses={hasPermission("inventory.warehouse.read")}
      />
    </ChannelPermissionBoundary>
  );
}

function ChannelOrdersRoute() {
  const navigate = useNavigate();
  const { hasPermission } = useSession();
  return (
    <ChannelPermissionBoundary permission={P.orderRead}>
      <ChannelOrdersPage
        canRetry={hasPermission(P.orderRetry)}
        canReadRaw={hasPermission(P.orderRawRead)}
        canReadChannels={hasPermission(P.channelRead)}
        {...(hasPermission("sales.order.read")
          ? {
              onOpenSalesOrder: (salesOrderId: string) =>
                void navigate({
                  to: "/sales/orders/$salesOrderId",
                  params: { salesOrderId },
                } as never),
            }
          : {})}
      />
    </ChannelPermissionBoundary>
  );
}

function ReconciliationRoute() {
  return (
    <ChannelPermissionBoundary permission={P.reconciliationRead}>
      <ReconciliationPage />
    </ChannelPermissionBoundary>
  );
}

export function channelsRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "channels/list",
      component: ChannelsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "channels/orders",
      component: ChannelOrdersRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "channels/reconciliation",
      component: ReconciliationRoute,
    }),
  ] as AnyRoute[];
}
